import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DashboardSettingsPanel - Settings dropdown for toggling dashboard sections
 */
import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaCog, FaEye, FaUndo } from 'react-icons/fa';
const Container = styled.div `
  position: relative;
  display: inline-block;
`;
const SettingsButton = styled.button `
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.$isOpen ? 'var(--bg-tertiary)' : 'var(--bg-secondary)'};
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-color-hover);
  }

  svg {
    transition: transform 0.2s;
    transform: ${props => props.$isOpen ? 'rotate(90deg)' : 'rotate(0)'};
  }
`;
const Badge = styled.span `
  background: #3b82f6;
  color: white;
  font-size: 0.7rem;
  padding: 0.15rem 0.4rem;
  border-radius: 8px;
  font-weight: 600;
`;
const Dropdown = styled.div `
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s;
`;
const DropdownHeader = styled.div `
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
`;
const DropdownTitle = styled.h4 `
  margin: 0 0 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
`;
const DropdownSubtitle = styled.p `
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;
const SectionList = styled.div `
  padding: 0.5rem 0;
  max-height: 300px;
  overflow-y: auto;
`;
const SectionItem = styled.label `
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--bg-tertiary);
  }
`;
const Checkbox = styled.div `
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.checked ? '#3b82f6' : 'var(--border-color)'};
  border-radius: 4px;
  background: ${props => props.checked ? '#3b82f6' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;

  svg {
    color: white;
    font-size: 0.7rem;
  }
`;
const SectionInfo = styled.div `
  flex: 1;
  min-width: 0;
`;
const SectionLabel = styled.div `
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;
const SectionDescription = styled.div `
  font-size: 0.75rem;
  color: var(--text-tertiary);
`;
const DropdownFooter = styled.div `
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  gap: 0.5rem;
`;
const FooterButton = styled.button `
  flex: 1;
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  transition: all 0.15s;

  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;
const DashboardSettingsPanel = ({ sectionVisibility, sections, onToggleSection, onShowAll, onReset, visibleCount, totalCount }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    const allVisible = visibleCount === totalCount;
    return (_jsxs(Container, { ref: containerRef, children: [_jsxs(SettingsButton, { "$isOpen": isOpen, onClick: () => setIsOpen(!isOpen), children: [_jsx(FaCog, {}), _jsx("span", { children: "Sections" }), !allVisible && _jsxs(Badge, { children: [visibleCount, "/", totalCount] })] }), _jsxs(Dropdown, { "$isOpen": isOpen, children: [_jsxs(DropdownHeader, { children: [_jsx(DropdownTitle, { children: "Dashboard Sections" }), _jsxs(DropdownSubtitle, { children: ["Toggle sections to customize your view. ", visibleCount, " of ", totalCount, " visible."] })] }), _jsx(SectionList, { children: sections.map((section) => {
                            const isVisible = sectionVisibility[section.key];
                            return (_jsxs(SectionItem, { onClick: () => onToggleSection(section.key), children: [_jsx(Checkbox, { checked: isVisible, children: isVisible && _jsx(FaEye, {}) }), _jsxs(SectionInfo, { children: [_jsx(SectionLabel, { children: section.label }), _jsx(SectionDescription, { children: section.description })] })] }, section.key));
                        }) }), _jsxs(DropdownFooter, { children: [_jsxs(FooterButton, { onClick: onShowAll, children: [_jsx(FaEye, {}), " Show All"] }), _jsxs(FooterButton, { onClick: onReset, children: [_jsx(FaUndo, {}), " Reset"] })] })] })] }));
};
export default DashboardSettingsPanel;
